import { useCallback } from "react";
import { X } from "lucide-react";
import { clockApi, type Row } from "../../../api/clock";
import { fmtDate } from "../../../utils/labels";
import { LoadState, useLoad } from "../shared";

const num = (v: unknown, d = 1) => (typeof v === "number" ? v.toFixed(d) : "—");

export function EvidencePanel({ requestId, onClose }: { requestId: string; onClose: () => void }) {
  const loader = useCallback(() => clockApi.evidence(requestId), [requestId]);
  const { data, error } = useLoad<Row[]>(loader);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <aside data-testid="evidence-panel" onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-xl overflow-y-auto border-l border-white/10 bg-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <div><p className="label-caps text-signal">Admin only</p><h2 className="font-display text-2xl font-bold uppercase text-white">Location evidence</h2></div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="evidence-close-button" className="p-2 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-4 text-xs text-zinc-500">Precise coordinates are purged after the retention period. The pass/fail result stays on the punch request.</p>
        <LoadState error={error} loading={!data && !error} testId="evidence-load-state" />
        {data?.length === 0 && <p data-testid="evidence-empty" className="text-sm text-zinc-400">No location submitted for this request.</p>}
        <ul className="space-y-3">
          {data?.map((e) => (
            <li key={e.id} data-testid={`evidence-item-${e.id}`} className="rounded-sm border border-white/10 p-4 text-xs text-zinc-300">
              <p className="mb-2 font-mono text-sm text-white">{String(e.outcome)}</p>
              <dl className="grid grid-cols-2 gap-1">
                <dt className="text-zinc-500">Lat / Lng</dt><dd className="font-mono">{num(e.latitude, 6)}, {num(e.longitude, 6)}</dd>
                <dt className="text-zinc-500">Accuracy</dt><dd>{num(e.accuracy_m)} m</dd>
                <dt className="text-zinc-500">Distance</dt><dd>{num(e.distance_m)} m</dd>
                <dt className="text-zinc-500">Position time</dt><dd>{fmtDate(e.position_timestamp as string)}</dd>
                <dt className="text-zinc-500">Received</dt><dd>{fmtDate(e.received_at as string)}</dd>
                <dt className="text-zinc-500">IP</dt><dd className="font-mono">{String(e.ip ?? "—")}</dd>
                <dt className="text-zinc-500">Failure</dt><dd>{String(e.failure_reason ?? "—")}</dd>
              </dl>
              <p className="mt-2 truncate text-[10px] text-zinc-500">{String(e.user_agent ?? "")}</p>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
